/**
 * Containers Store - Zustand
 *
 * @module core/modules/waste/store
 *
 * @description
 * Gestiona el estado de contenedores del módulo Waste.
 * Incluye niveles de llenado en tiempo real.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { WasteContainer, ContainerFillUpdate } from '../types';

/* ===== TYPES ===== */

export interface ContainersState {
  /**
   * Lista de contenedores cargados
   */
  containers: WasteContainer[];

  /**
   * Estado de carga
   */
  isLoading: boolean;

  /**
   * Error de carga
   */
  error: string | null;
}

export interface ContainersActions {
  /**
   * Cargar contenedores
   */
  setContainers: (containers: WasteContainer[]) => void;

  /**
   * Agregar contenedor
   */
  addContainer: (container: WasteContainer) => void;

  /**
   * Actualizar contenedor
   */
  updateContainer: (id: string, updates: Partial<WasteContainer>) => void;

  /**
   * Eliminar contenedor
   */
  removeContainer: (id: string) => void;

  /**
   * Actualizar nivel de llenado en tiempo real
   */
  updateFillLevel: (update: ContainerFillUpdate) => void;

  /**
   * Estado de carga
   */
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  /**
   * Limpiar store
   */
  clear: () => void;
}

export type ContainersStore = ContainersState & ContainersActions;

/* ===== INITIAL STATE ===== */

const initialState: ContainersState = {
  containers: [],
  isLoading: false,
  error: null,
};

/* ===== STORE ===== */

export const useContainersStore = create<ContainersStore>()(
  devtools(
    (set) => ({
      ...initialState,

      setContainers: (containers) => {
        set({ containers, isLoading: false, error: null });
      },

      addContainer: (container) => {
        set((state) => ({
          containers: [...state.containers, container],
        }));
      },

      updateContainer: (id, updates) => {
        set((state) => ({
          containers: state.containers.map((c) => (c.id === id ? { ...c, ...updates } : c)),
        }));
      },

      removeContainer: (id) => {
        set((state) => ({
          containers: state.containers.filter((c) => c.id !== id),
        }));
      },

      updateFillLevel: (update) => {
        set((state) => ({
          containers: state.containers.map((c) =>
            c.id === update.containerId
              ? { ...c, currentFill: update.fillLevel, status: update.status }
              : c
          ),
        }));
      },

      setLoading: (loading) => set({ isLoading: loading }),
      setError: (error) => set({ error, isLoading: false }),

      clear: () => set(initialState),
    }),
    {
      name: 'waste-containers-store',
    }
  )
);

/* ===== SELECTORS ===== */

export const selectContainers = (state: ContainersStore): WasteContainer[] => state.containers;
export const selectContainerById = (id: string) => (state: ContainersStore): WasteContainer | undefined =>
  state.containers.find((c) => c.id === id);
export const selectContainersByZone = (zoneId: string) => (state: ContainersStore): WasteContainer[] =>
  state.containers.filter((c) => c.zone?.id === zoneId);
export const selectFullContainers = (state: ContainersStore): WasteContainer[] =>
  state.containers.filter((c) => c.status === 'full' || (c.currentFill && c.currentFill >= 90));
export const selectIsLoading = (state: ContainersStore): boolean => state.isLoading;
export const selectError = (state: ContainersStore): string | null => state.error;
